// Serviço de identidade — camada única de acesso a dados dos domínios
// identity/organizations. Páginas nunca fazem query direta ao Supabase.
import { createClient } from '@/lib/supabase/client'
import type { Tables } from '@/lib/database.types'
import type { MeuWorkspace, PerfilUsuario, Workspace, WorkspaceMembro } from '@/lib/types'

export async function getPerfil(userId: string): Promise<PerfilUsuario | null> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('perfis_usuario')
    .select('user_id, nome, email, telefone, crm, especialidades')
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  return (data as PerfilUsuario | null) ?? null
}

export async function checkPlatformAdmin(): Promise<boolean> {
  const supabase = createClient()
  const { data, error } = await supabase.rpc('eu_sou_platform_admin')
  if (error) return false
  return data === true
}

/** Admin da plataforma abre um workspace sem membership — membro virtual titular. */
export async function getWorkspaceComoAdmin(
  workspaceId: string,
  adminUserId: string
): Promise<MeuWorkspace | null> {
  const supabase = createClient()
  const { data: ws, error } = await supabase
    .from('workspaces')
    .select('id, nome, medico_titular, status')
    .eq('id', workspaceId)
    .maybeSingle()
  if (error) throw error
  if (!ws) return null

  const membroVirtual: WorkspaceMembro = {
    id: 'platform-admin',
    workspace_id: ws.id,
    user_id: adminUserId,
    papel: 'medico_titular',
    clinica_scope_id: null,
    permissoes: {},
    convite_email: null,
    convite_status: 'aceito',
    ativo: true,
  }

  return { workspace: ws as Workspace, membro: membroVirtual }
}

export async function getMeusWorkspaces(userId: string): Promise<MeuWorkspace[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('workspace_membros')
    .select(
      'id, workspace_id, user_id, papel, clinica_scope_id, permissoes, convite_email, convite_status, ativo, workspaces(id, nome, medico_titular, status)'
    )
    .eq('user_id', userId)
    .eq('ativo', true)
    .eq('convite_status', 'aceito')
  if (error) throw error

  return (data ?? []).flatMap(row => {
    const raw = (row as Record<string, unknown>).workspaces
    const ws = (Array.isArray(raw) ? raw[0] : raw) as Workspace | undefined
    if (!ws || ws.status !== 'ativo') return []
    const { workspaces: _drop, ...membro } = row as Record<string, unknown>
    void _drop
    const m = membro as Tables<'workspace_membros'>
    return [{
      workspace: ws,
      membro: {
        ...m,
        permissoes: (m.permissoes ?? {}) as Record<string, boolean>,
        convite_status: m.convite_status as WorkspaceMembro['convite_status'],
        papel: m.papel as WorkspaceMembro['papel'],
      },
    }]
  })
}

/** Onboarding: cria perfil + workspace + membro titular atomicamente (RPC). */
export async function criarWorkspace(params: {
  nomeWorkspace: string
  nomeMedico: string
  crm: string | null
  especialidades: string[]
}): Promise<string> {
  const supabase = createClient()
  const { data, error } = await supabase.rpc('criar_workspace', {
    p_nome_workspace: params.nomeWorkspace,
    p_nome: params.nomeMedico,
    p_crm: params.crm,
    p_especialidades: params.especialidades,
  })
  if (error) throw error
  return data as string
}

export async function aceitarConvite(token: string): Promise<string> {
  const supabase = createClient()
  const { data, error } = await supabase.rpc('aceitar_convite', { p_token: token })
  if (error) throw error
  return data as string
}

export async function registrarAuditoria(params: {
  workspaceId: string
  acao: string
  entidade: string
  registroId?: string | null
  dados?: Record<string, unknown>
}) {
  try {
    const supabase = createClient()
    await supabase.from('audit_logs').insert({
      workspace_id: params.workspaceId,
      acao: params.acao,
      entidade: params.entidade,
      registro_id: params.registroId ?? null,
      dados: params.dados ?? {},
    })
  } catch {
    // Auditoria nunca bloqueia a ação principal.
  }
}
